import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../redux/auth/authActions';
import securedInstance from '../../api/securedInstance';
import { fetchProgramList } from '../redux/actions/Actions';
import axiosinstance from '../../api/axiosinstance';

const STATUS_COLORS = {
  'New':            { bg:'#DBEAFE', color:'#2563EB', border:'#93C5FD' },
  'Follow Up':      { bg:'#FEF3C7', color:'#D97706', border:'#FCD34D' },
  'Converted':      { bg:'#D1FAE5', color:'#059669', border:'#6EE7B7' },
  'Not Interested': { bg:'#FEE2E2', color:'#DC2626', border:'#FCA5A5' },
};

const Badge = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg:'#F1F5F9', color:'#64748B', border:'#E2E8F0' };
  return <span style={{ fontSize:11.5, fontWeight:700, padding:'4px 12px', borderRadius:50, background:c.bg, color:c.color, border:`1px solid ${c.border}`, whiteSpace:'nowrap' }}>{status}</span>;
};

const SOURCES = ['All', 'Website', 'Instagram', 'Facebook', 'Google Ads', 'LinkedIn', 'Referral', 'College Drive'];
const STATUS_LIST = ['New', 'Follow Up', 'Converted', 'Not Interested'];

const HRDashboard = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user }  = useSelector(s => s.auth);
  const programs  = useSelector(s => s.fetchAllPrograms?.programs || []);

  const [enquiries,  setEnquiries]   = useState([]);
  const [stats,      setStats]       = useState({});
  const [loading,    setLoading]     = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [addOpen,    setAddOpen]     = useState(false);

  // Filters
  const [search,       setSearch]       = useState('');
  const [progFilter,   setProgFilter]   = useState('All');
  const [courseFilter,  setCourseFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [page,         setPage]         = useState(1);
  const PER_PAGE = 10;

  // Add enquiry form
  const [newEnq, setNewEnq] = useState({ student_name:'', email:'', phone:'', program:'', course:'', source:'Website', status:'New', notes:'' });
  const [courses, setCourses] = useState([]);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      securedInstance.get('/api/enquiries'),
      securedInstance.get('/api/enquiries/stats'),
      securedInstance.get('/api/callback').catch(() => ({ data: [] })),
    ]).then(([enqRes, statsRes, cbRes]) => {
      const enqData = enqRes.data || [];
      const cbData  = cbRes.data  || [];

      const enqPhones = new Set(enqData.map(e => e.phone));
      const missingCallbacks = cbData
        .filter(c => c.phone && !enqPhones.has(c.phone))
        .map(c => ({
          enquiry_id: `cb-${c.id}`,
          student_name: 'Callback - ' + c.phone,
          email: '',
          phone: c.phone,
          program: c.program || '',
          course: c.course || '',
          source: 'Website',
          status: 'New',
          created_at: c.created_at,
          _isCallback: true,
        }));

      setEnquiries([...enqData, ...missingCallbacks]);

      const total = (statsRes.data?.total || 0) + missingCallbacks.length;
      const newCount = (statsRes.data?.new_count || 0) + missingCallbacks.length;
      setStats({ ...statsRes.data, total, new_count: newCount });
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { dispatch(fetchProgramList()); fetchAll(); }, []);

  // Filter logic
  const filtered = enquiries.filter(e => {
    if (progFilter !== 'All' && e.program !== progFilter) return false;
    if (courseFilter !== 'All' && e.course !== courseFilter) return false;
    if (statusFilter !== 'All' && e.status !== statusFilter) return false;
    if (dateFrom) {
      const d = new Date(e.created_at);
      if (isNaN(d) || d < new Date(dateFrom + 'T00:00:00')) return false;
    }
    if (dateTo) {
      const d = new Date(e.created_at);
      if (isNaN(d) || d > new Date(dateTo + 'T23:59:59')) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(e.student_name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q) || e.phone?.includes(q) || e.course?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const allCourses = ['All', ...new Set(enquiries.map(e => e.course).filter(Boolean))];
  const allPrograms = ['All', ...new Set(enquiries.map(e => e.program).filter(Boolean))];

  useEffect(() => { setPage(1); }, [search, progFilter, courseFilter, statusFilter, dateFrom, dateTo]);

  const updateStatus = async (id, newStatus) => {
    if (String(id).startsWith('cb-')) {
      const cbEntry = enquiries.find(e => e.enquiry_id === id);
      if (cbEntry) {
        try {
          const res = await securedInstance.post('/api/enquiries', {
            student_name: cbEntry.student_name, phone: cbEntry.phone, program: cbEntry.program,
            course: cbEntry.course, source: cbEntry.source, status: newStatus,
          });
          fetchAll();
        } catch {}
      }
      return;
    }
    try {
      await securedInstance.put(`/api/enquiries/${id}`, { status: newStatus });
      setEnquiries(prev => prev.map(e => e.enquiry_id === id ? { ...e, status: newStatus } : e));
      fetchAll();
    } catch {}
  };

  const handleAddEnquiry = async () => {
    if (!newEnq.student_name.trim() || !newEnq.phone.trim()) return;
    try {
      await securedInstance.post('/api/enquiries', newEnq);
      setAddOpen(false);
      setNewEnq({ student_name:'', email:'', phone:'', program:'', course:'', source:'Website', status:'New', notes:'' });
      fetchAll();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    if (String(id).startsWith('cb-')) {
      setEnquiries(prev => prev.filter(e => e.enquiry_id !== id));
      return;
    }
    try {
      await securedInstance.delete(`/api/enquiries/${id}`);
      fetchAll();
    } catch {}
  };

  const handleProgramChange = (val) => {
    setNewEnq(p => ({ ...p, program: val, course: '' }));
    setCourses([]);
    const prog = programs.find(p => p.program_name === val);
    if (prog) {
      axiosinstance.get(`/api/category/all/${prog.program_id}`)
        .then(res => { if (Array.isArray(res.data)) setCourses(res.data); })
        .catch(() => {});
    }
  };

  const exportCSV = () => {
    const headers = ['Student Name','Email','Phone','Program','Course','Source','Status','Enquiry Date'];
    const rows = filtered.map(e => [
      e.student_name || '',
      e.email || '',
      e.phone || '',
      e.program || '',
      e.course || '',
      e.source || '',
      e.status || '',
      e.created_at ? new Date(e.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '',
    ]);
    const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fromLabel = dateFrom || 'all';
    const toLabel = dateTo || 'now';
    a.download = `enquiries_${fromLabel}_to_${toLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setQuickRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(to.toISOString().split('T')[0]);
  };

  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };

  const statCards = [
    { label:'Total Enquiries',  val: stats.total || 0,                icon:'👥', color:'#2563EB', bg:'#EFF6FF' },
    { label:'New Enquiries',    val: stats.new_count || 0,            icon:'✨', color:'#16A34A', bg:'#F0FDF4' },
    { label:'Follow Ups',       val: stats.followup_count || 0,       icon:'📞', color:'#0EA5E9', bg:'#F0F9FF' },
    { label:'Converted',        val: stats.converted_count || 0,      icon:'✅', color:'#059669', bg:'#ECFDF5' },
    { label:'Not Interested',   val: stats.not_interested_count || 0, icon:'❌', color:'#DC2626', bg:'#FEF2F2' },
  ];

  const selectStyle = { padding:'9px 14px', border:'1.5px solid #E2E8F0', borderRadius:8, fontSize:13, color:'#374151', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit' };

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif", background:'#F8FAFC', minHeight:'100vh' }}>

      {/* ── Top bar ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E2E8F0', padding:'0 32px', display:'flex', alignItems:'center', justifyContent:'space-between', height:60, position:'sticky', top:0, zIndex:100, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#2563EB,#1D4ED8)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:'#0F172A' }}>HR Dashboard</div>
            <div style={{ fontSize:11, color:'#94A3B8' }}>UPL Snipe — Enquiry Management</div>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => setAddOpen(true)} style={{ padding:'8px 18px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontFamily:'inherit' }}>
            + Add Enquiry
          </button>

          <div style={{ position:'relative' }}>
            <button onClick={() => setProfileOpen(o => !o)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 10px', background: profileOpen ? '#F1F5F9' : 'transparent', border:'1.5px solid #E2E8F0', borderRadius:10, cursor:'pointer' }}>
              <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#2563EB,#0EA5E9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff' }}>
                {(user?.username || 'H')[0].toUpperCase()}
              </div>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#0F172A', lineHeight:1.2 }}>{user?.username || 'HR'}</div>
                <div style={{ fontSize:10.5, color:'#94A3B8' }}>Human Resources</div>
              </div>
            </button>
            {profileOpen && (
              <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, background:'#fff', border:'1px solid #E2E8F0', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.12)', minWidth:200, zIndex:999, overflow:'hidden' }}
                onMouseLeave={() => setProfileOpen(false)}>
                <button onClick={() => { setProfileOpen(false); dispatch(logout()); navigate('/'); }}
                  style={{ width:'100%', padding:'13px 18px', display:'flex', alignItems:'center', gap:10, background:'none', border:'none', cursor:'pointer', fontSize:13.5, fontWeight:600, color:'#DC2626', fontFamily:'inherit' }}
                  onMouseEnter={e => e.currentTarget.style.background='#FEF2F2'}
                  onMouseLeave={e => e.currentTarget.style.background='none'}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1440, margin:'0 auto', padding:'28px 24px' }}>

        {/* ── Stats Cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:16, marginBottom:28 }}>
          {statCards.map(s => (
            <div key={s.label} style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:14, padding:'22px 24px', boxShadow:'0 1px 6px rgba(0,0,0,0.04)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:12.5, color:'#64748B', fontWeight:500, marginBottom:6 }}>{s.label}</div>
                <div style={{ fontSize:32, fontWeight:900, color:'#0F172A', letterSpacing:'-0.02em', lineHeight:1 }}>{s.val}</div>
              </div>
              <div style={{ width:48, height:48, borderRadius:12, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{s.icon}</div>
            </div>
          ))}
        </div>

        {/* ── Date Range & Export ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:20, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', border:'1.5px solid #E2E8F0', borderRadius:10, padding:'6px 14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ border:'none', outline:'none', fontSize:13, color:'#374151', fontFamily:'inherit', background:'transparent', cursor:'pointer' }} />
              <span style={{ color:'#94A3B8', fontSize:12, fontWeight:600 }}>to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ border:'none', outline:'none', fontSize:13, color:'#374151', fontFamily:'inherit', background:'transparent', cursor:'pointer' }} />
            </div>
            {['7', '30', '90'].map(d => (
              <button key={d} onClick={() => setQuickRange(Number(d))}
                style={{ padding:'7px 14px', border:'1.5px solid #E2E8F0', borderRadius:8, background:'#fff', fontSize:12, fontWeight:600, color:'#475569', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#2563EB'; e.currentTarget.style.color='#2563EB'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='#E2E8F0'; e.currentTarget.style.color='#475569'; }}
              >
                {d === '7' ? 'Last 7 days' : d === '30' ? 'Last 30 days' : 'Last 90 days'}
              </button>
            ))}
            {(dateFrom || dateTo) && (
              <button onClick={clearDateFilter} style={{ padding:'7px 12px', border:'none', borderRadius:8, background:'#FEE2E2', fontSize:12, fontWeight:600, color:'#DC2626', cursor:'pointer', fontFamily:'inherit' }}>
                Clear dates
              </button>
            )}
          </div>
          <button onClick={exportCSV} style={{ padding:'9px 20px', background:'#fff', border:'1.5px solid #2563EB', borderRadius:8, fontSize:13, fontWeight:700, color:'#2563EB', cursor:'pointer', display:'flex', alignItems:'center', gap:8, fontFamily:'inherit', transition:'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background='#2563EB'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.color='#2563EB'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export CSV
          </button>
        </div>

        {/* ── Enquiries Table ── */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E2E8F0', boxShadow:'0 2px 12px rgba(0,0,0,0.05)', overflow:'hidden' }}>

          {/* Toolbar */}
          <div style={{ padding:'18px 22px', borderBottom:'1px solid #F1F5F9', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <h2 style={{ fontSize:18, fontWeight:800, color:'#0F172A', margin:0, flex:1 }}>
              Enquiries List
              <span style={{ fontSize:12, fontWeight:500, color:'#94A3B8', marginLeft:8 }}>({filtered.length} entries)</span>
            </h2>

            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#F8FAFC', border:'1.5px solid #E2E8F0', borderRadius:8, padding:'8px 12px', minWidth:200 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone..."
                style={{ border:'none', outline:'none', background:'transparent', fontSize:13, color:'#1E293B', width:'100%', fontFamily:'inherit' }}/>
            </div>

            <select value={progFilter} onChange={e => setProgFilter(e.target.value)} style={selectStyle}>
              {allPrograms.map(p => <option key={p} value={p}>{p === 'All' ? 'All Programs' : p}</option>)}
            </select>
            <select value={courseFilter} onChange={e => setCourseFilter(e.target.value)} style={selectStyle}>
              {allCourses.map(c => <option key={c} value={c}>{c === 'All' ? 'All Courses' : c}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="All">All Status</option>
              {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign:'center', padding:'60px 0' }}>
              <div style={{ width:40, height:40, border:'3px solid #E2E8F0', borderTopColor:'#2563EB', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 0', color:'#94A3B8', fontSize:15 }}>No enquiries found</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13.5 }}>
                <thead>
                  <tr style={{ background:'#F8FAFC', borderBottom:'1.5px solid #E2E8F0' }}>
                    {['Student Name','Mobile Number','Program','Course','Source','Enquiry Date','Status','Action'].map(h => (
                      <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:11.5, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((e, i) => (
                    <tr key={e.enquiry_id} style={{ borderBottom:'1px solid #F1F5F9', background: i % 2 === 0 ? '#fff' : '#FAFBFF', transition:'background 0.15s' }}
                      onMouseEnter={ev => ev.currentTarget.style.background='#F0F7FF'}
                      onMouseLeave={ev => ev.currentTarget.style.background= i % 2 === 0 ? '#fff' : '#FAFBFF'}
                    >
                      {/* Name + Email */}
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                          <div style={{ width:38, height:38, borderRadius:'50%', background:'linear-gradient(135deg,#2563EB,#0EA5E9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>
                            {(e.student_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, color:'#0F172A', fontSize:13.5 }}>{e.student_name}</div>
                            <div style={{ fontSize:11.5, color:'#94A3B8', marginTop:1 }}>{e.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      {/* Phone */}
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontWeight:600, color:'#0F172A' }}>+91 {e.phone}</span>
                          <a href={`https://wa.me/91${e.phone}`} target="_blank" rel="noreferrer" title="WhatsApp" style={{ color:'#22C55E', display:'flex' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </a>
                        </div>
                      </td>
                      <td style={{ padding:'14px 16px', color:'#374151' }}>{e.program || '—'}</td>
                      <td style={{ padding:'14px 16px', color:'#374151', maxWidth:180 }}>{e.course || '—'}</td>
                      <td style={{ padding:'14px 16px', color:'#64748B' }}>{e.source || '—'}</td>
                      <td style={{ padding:'14px 16px', color:'#64748B', whiteSpace:'nowrap', fontSize:12.5 }}>
                        {e.created_at ? new Date(e.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                      </td>
                      {/* Status dropdown */}
                      <td style={{ padding:'14px 16px' }}>
                        <select value={e.status} onChange={ev => updateStatus(e.enquiry_id, ev.target.value)}
                          style={{ fontSize:11.5, fontWeight:700, padding:'4px 10px', borderRadius:50, border:`1px solid ${(STATUS_COLORS[e.status] || {}).border || '#E2E8F0'}`, background:(STATUS_COLORS[e.status] || {}).bg || '#F1F5F9', color:(STATUS_COLORS[e.status] || {}).color || '#64748B', cursor:'pointer', outline:'none', fontFamily:'inherit' }}>
                          {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      {/* Actions */}
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <a href={`tel:+91${e.phone}`} title="Call" style={{ width:32, height:32, borderRadius:'50%', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', color:'#2563EB', textDecoration:'none' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                          </a>
                          <a href={`https://wa.me/91${e.phone}`} target="_blank" rel="noreferrer" title="WhatsApp" style={{ width:32, height:32, borderRadius:'50%', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', color:'#22C55E', textDecoration:'none' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </a>
                          <button onClick={() => handleDelete(e.enquiry_id)} title="Delete" style={{ width:32, height:32, borderRadius:'50%', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', color:'#DC2626', cursor:'pointer', background:'none' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding:'14px 22px', borderTop:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <span style={{ fontSize:13, color:'#64748B' }}>
                Showing {(page-1)*PER_PAGE+1} to {Math.min(page*PER_PAGE, filtered.length)} of {filtered.length} entries
              </span>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                  style={{ padding:'7px 14px', border:'1px solid #E2E8F0', borderRadius:8, fontSize:13, cursor:'pointer', background:'#fff', color: page===1?'#CBD5E1':'#374151', fontFamily:'inherit' }}>
                  &lt;
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (p > totalPages) return null;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      style={{ padding:'7px 14px', border:'1px solid', borderRadius:8, fontSize:13, cursor:'pointer', fontFamily:'inherit', background: page===p?'#2563EB':'#fff', color: page===p?'#fff':'#374151', borderColor: page===p?'#2563EB':'#E2E8F0', fontWeight: page===p?700:400 }}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                  style={{ padding:'7px 14px', border:'1px solid #E2E8F0', borderRadius:8, fontSize:13, cursor:'pointer', background:'#fff', color: page===totalPages?'#CBD5E1':'#374151', fontFamily:'inherit' }}>
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Enquiry Modal ── */}
      {addOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setAddOpen(false); }}>
          <div style={{ background:'#fff', borderRadius:16, padding:'28px 32px', width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontSize:20, fontWeight:800, color:'#0F172A', margin:0 }}>Add New Enquiry</h3>
              <button onClick={() => setAddOpen(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94A3B8', lineHeight:1 }}>✕</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Student Name *</label>
                <input value={newEnq.student_name} onChange={e => setNewEnq(p => ({ ...p, student_name: e.target.value }))}
                  style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:8, fontSize:13, boxSizing:'border-box', fontFamily:'inherit' }} placeholder="Full name" />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Email</label>
                <input value={newEnq.email} onChange={e => setNewEnq(p => ({ ...p, email: e.target.value }))}
                  style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:8, fontSize:13, boxSizing:'border-box', fontFamily:'inherit' }} placeholder="email@example.com" />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Phone *</label>
                <input value={newEnq.phone} onChange={e => setNewEnq(p => ({ ...p, phone: e.target.value }))}
                  style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:8, fontSize:13, boxSizing:'border-box', fontFamily:'inherit' }} placeholder="9876543210" maxLength={10} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Program</label>
                <select value={newEnq.program} onChange={e => handleProgramChange(e.target.value)}
                  style={{ ...selectStyle, width:'100%', boxSizing:'border-box' }}>
                  <option value="">-- Select --</option>
                  {programs.map(p => <option key={p.program_id} value={p.program_name}>{p.program_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Course</label>
                <select value={newEnq.course} onChange={e => setNewEnq(p => ({ ...p, course: e.target.value }))}
                  style={{ ...selectStyle, width:'100%', boxSizing:'border-box' }} disabled={!newEnq.program}>
                  <option value="">{newEnq.program ? '-- Select --' : '-- Pick program first --'}</option>
                  {courses.map(c => <option key={c.course_id} value={c.course_name}>{c.course_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Source</label>
                <select value={newEnq.source} onChange={e => setNewEnq(p => ({ ...p, source: e.target.value }))}
                  style={{ ...selectStyle, width:'100%', boxSizing:'border-box' }}>
                  {SOURCES.filter(s => s !== 'All').map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Status</label>
                <select value={newEnq.status} onChange={e => setNewEnq(p => ({ ...p, status: e.target.value }))}
                  style={{ ...selectStyle, width:'100%', boxSizing:'border-box' }}>
                  {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>Notes</label>
                <textarea value={newEnq.notes} onChange={e => setNewEnq(p => ({ ...p, notes: e.target.value }))} rows={2}
                  style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #E2E8F0', borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} placeholder="Optional notes..." />
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
              <button onClick={() => setAddOpen(false)} style={{ padding:'10px 24px', border:'1.5px solid #E2E8F0', borderRadius:8, background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', color:'#475569' }}>Cancel</button>
              <button onClick={handleAddEnquiry} style={{ padding:'10px 24px', border:'none', borderRadius:8, background:'#2563EB', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Add Enquiry</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRDashboard;
